// GenerationCards.tsx
"use client";

import React, { FC, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { useAppSelector } from "@/redux/store";
import { fetchCtaVersions, CtaVersionType } from "utils/cta";
import { Image as TImage } from "@/types/thread";
import GenerationCard from "./GenerationCard";

interface GenerationCardsProps {
  images: TImage[];
  onImageClick: (index: number) => void;
  modelUsed: string;
  editEnabled?: boolean;
}

const GenerationCards: FC<GenerationCardsProps> = ({
  images,
  onImageClick,
  modelUsed,
  editEnabled = false,
}) => {
  const user = useAppSelector((state) => state.user.user);
  const isSubscribed = Boolean(user && user.subscriptionType !== "free");
  const [ctaVersion, setCtaVersion] = useState<CtaVersionType | null>(null);

  useEffect(() => {
    if (!isSubscribed) {
      // Randomly choose "A" or "B"
      const prefix = Math.random() < 0.5 ? "A" : "B";
      fetchCtaVersions("locked_generations_on_click", prefix)
        .then((version) => {
          if (version) {
            setCtaVersion(version);
          } else {
            console.error(
              "GenerationCards: No CTA version found for prefix",
              prefix
            );
          }
        })
        .catch((err) =>
          console.error("GenerationCards: Error fetching CTA version:", err)
        );
    }
  }, [isSubscribed]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {images.length === 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
          <GenerationCard
            image={images[0]}
            onImageClick={() => onImageClick(0)}
            editEnabled={editEnabled}
            isSingleImage
            ctaVersion={ctaVersion}
            imageIndex={0}
            totalImages={images.length}
          />
        </Box>
      )}
      {images.length === 2 && (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
          <GenerationCard
            image={images[0]}
            onImageClick={() => onImageClick(0)}
            editEnabled={editEnabled}
            ctaVersion={ctaVersion}
            imageIndex={0}
            totalImages={images.length}
          />
          <GenerationCard
            image={images[1]}
            onImageClick={() => onImageClick(1)}
            editEnabled={editEnabled}
            ctaVersion={ctaVersion}
            imageIndex={1}
            totalImages={images.length}
          />
        </Box>
      )}
      {images.length === 3 && (
        <>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
            <GenerationCard
              image={images[0]}
              onImageClick={() => onImageClick(0)}
              editEnabled={editEnabled}
              ctaVersion={ctaVersion}
              imageIndex={0}
              totalImages={images.length}
            />
            <GenerationCard
              image={images[1]}
              onImageClick={() => onImageClick(1)}
              editEnabled={editEnabled}
              ctaVersion={ctaVersion}
              imageIndex={1}
              totalImages={images.length}
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
            <GenerationCard
              image={images[2]}
              onImageClick={() => onImageClick(2)}
              editEnabled={editEnabled}
              ctaVersion={ctaVersion}
              imageIndex={2}
              totalImages={images.length}
            />
          </Box>
        </>
      )}
      {images.length === 4 && (
        <>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
            <GenerationCard
              image={images[0]}
              onImageClick={() => onImageClick(0)}
              editEnabled={editEnabled}
              ctaVersion={ctaVersion}
              imageIndex={0}
              totalImages={images.length}
            />
            <GenerationCard
              image={images[1]}
              onImageClick={() => onImageClick(1)}
              editEnabled={editEnabled}
              ctaVersion={ctaVersion}
              imageIndex={1}
              totalImages={images.length}
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
            <GenerationCard
              image={images[2]}
              onImageClick={() => onImageClick(2)}
              editEnabled={editEnabled}
              ctaVersion={ctaVersion}
              imageIndex={2}
              totalImages={images.length}
            />
            <GenerationCard
              image={images[3]}
              onImageClick={() => onImageClick(3)}
              editEnabled={editEnabled}
              ctaVersion={ctaVersion}
              imageIndex={3}
              totalImages={images.length}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default GenerationCards;
